<script setup lang="ts">
import DestinationInput from "@/components/GlobalHeader/DestinationInput.vue";
import CustomButton from "@/components/Button/Button.vue";
import DateInput from "@/components/Global/DateInput.vue";
import PassengerInput from "@/components/GlobalHeader/PassengerInput.vue";
import {computed, onMounted, onUnmounted, ref} from 'vue';
import {useRoute} from "vue-router";
import {useI18n} from "vue-i18n";
import {useSearchStore} from "@/stores/searchStore";
import {useHomepageStore} from "@/stores/homepageStore";
import CategorySelection from "@/components/GlobalHeader/CategorySelection.vue";
import {useMatches} from "vue-responsiveness";
import BookingSearchModal from "@/components/BookingComponent/mobile/BookingSearchModal.vue";
import CustomModal from "@/components/CustomModal/CustomModal.vue";
import BookingPassengerPicker from "@/components/BookingComponent/BookingPassengerPicker.vue";
import {useUserStore} from "@/stores/userStore";
import {useWindowSize} from "@vueuse/core";

const {t} = useI18n(); // Add useI18n to use translations
const route = useRoute();
const userStore = useUserStore();
const searchStore = useSearchStore();
const homepageStore = useHomepageStore();
import { useRouteHelpers } from "@/composables/useRouteHelp";

const { isRecommendationsPage } = useRouteHelpers();
const showBookingModal = ref(false);
const matches = useMatches()

const inputWidth = computed(() => matches.isMin('min') ? 'w-[100%S]' : 'w-[25vw]');

onMounted(async () => {
  console.log("On Header mounted")
  await searchStore.handleRoute(route)

  //2025-01-16 TODO Refactor this
  homepageStore.reset()
  await homepageStore.getCategories()
  homepageStore.selections.location = searchStore.filters.destinations.find(d => d.value === searchStore.selectedFilters.destinations[0])
  homepageStore.selections.dates = [searchStore.selectedFilters.dateFrom, searchStore.selectedFilters.dateTo]
  homepageStore.selections.category = searchStore.filters.categories.find(c => c.id === searchStore.selectedFilters.categories[0])
  homepageStore.selections.passengers = searchStore.selectedFilters.passengers
  homepageStore.selections.cabins = searchStore.selectedFilters.cabins[0]
  homepageStore.selections.skipper = searchStore.selectedFilters.shouldHaveSkipper

})


</script>

<template>
  <div>
    <!-- Desktop View -->
    <div
        v-if="isRecommendationsPage"
        class="bg-white w-full"
        v-show="$matches.sm.min"
    >
      <div class="container flex items-center justify-between gap-4 px-4 w-full">
        <!-- Destination Input -->
        <DestinationInput
            v-model="searchStore.selectedFilters.destinations"
            class="flex-1 w-14vw"
        />

        <!-- Date Input -->
        <DateInput
            v-model:dateFrom="searchStore.selectedFilters.dateFrom"
            v-model:dateTo="searchStore.selectedFilters.dateTo"
            :initial-tab="searchStore.selectedFilters.dateTab"
            class="flex-1 max-w-xs"
            aria-label="Date Input"
            :columns="1"
            :rows="1"
            :calendarClass="'scale-[1.0]'"
        />

        <!-- Passenger Input -->
        <PassengerInput
            v-model:cabins="searchStore.selectedFilters.cabins"
            v-model:passengers="searchStore.selectedFilters.passengers"
            v-model:skipper="searchStore.selectedFilters.shouldHaveSkipper"
            class="flex-1 w-10vw"
            aria-label="Passenger Input"
        />

        <!-- Category Selection -->
        <CategorySelection
            v-model="searchStore.selectedFilters.categories"
            class="flex-1 w-10vw"
            aria-label="Category Selection"
        />

        <!-- Refresh Button -->
        <div class="flex-shrink-0">
          <CustomButton
              type="primary py-1 px-4"
              :rounded="true"
              :outlined="false"
              @click="$router.push({ path: '/recommendations', query: searchStore.recommendationQuery, force: true }).then(() => $router.go(0));"
          >
            <div class="text-sm">{{ t('search.refresh') }}</div>
          </CustomButton>
        </div>
      </div>
    </div>

      <!-- Mobile Booking Search Modal -->
    <div class="sm:hidden">
      <div
          class="shadow w-full bg-gradient-to-tr from-gradients-ghostWhite to-gradients-aliceBlue py-4 sticky min-h-18"
          @click="isRecommendationsPage ? (showBookingModal = true) : null"
          :class="{ 'hidden': !isRecommendationsPage }"
      >
        <div class="container">
          <div class="header-search-content pl-8 pr-5 flex flex-col gap-1 border-b border-black">
            <div class="responsive-body-medium font-normal">
              {{ searchStore.filters.destinations.find(d => d.value === searchStore.selectedFilters.destinations[0])?.label || t('booking.location') }}
            </div>
            <div class="flex input-data items-center flex-wrap gap-1">
              <div class="font-normal responsive-body-xsmall">
                {{ homepageStore.selections.dates.length == 2
                  ? `${userStore.formatDate(searchStore.selectedFilters.dateFrom)} - ${userStore.formatDate(searchStore.selectedFilters.dateTo)}`
                  : t('boat.selectDate') }}
              </div>
              <div class="font-normal responsive-body-xsmall">
                {{ homepageStore.selections.cabins
                  ? t('booking.cabins', { count: searchStore.recommendationQuery.cabins })
                  : t('booking.cabinsDefault') }}
              </div>
              <div class="font-normal responsive-body-xsmall">
                {{ homepageStore.selections.category?.name || t('booking.boatType') }}

              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile Booking Search Modal -->
      <CustomModal
          :show="showBookingModal"
          @close="showBookingModal = false"
      >
        <BookingSearchModal
            :refresh="true"
            @close="showBookingModal = false"
            @on-refresh="showBookingModal = false;$router.push({ path: '/recommendations', query: homepageStore.query, force: true }).then(() => $router.go(0));"
        />
      </CustomModal>
    </div>
  </div>
</template>

<style>


@media (max-width: 1440px) {
  .input-data div:not(:last-child)::after {
    content: '•';
    padding: 0 .5rem;
    color: #B7B6BE;
  }

  .input-data div::after {
    font-size: 0.5rem;
  }

  .header-search-content::before {
    position: absolute;
    content: '';
    background: url(@/assets/icons/svg/Search_24x24.svg) no-repeat;
    width: 30px;
    height: 30px;
    top: 45%;
    left: 2%;
    transform: translateY(-50%);
  }
}

.v-field--variant-filled .v-field__outline::before, .v-field--variant-underlined .v-field__outline::before {
  opacity: 1 !important;
}
</style>
